import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { FaCalendarAlt, FaSearch, FaChartLine } from 'react-icons/fa';
import ReportGroup from '../../components/reports/ReportGroup';

const ReportsPage = () => {
    const [activeTab, setActiveTab] = useState('period');

    // --- State for Period Analysis Tab ---
    const [singleStartDate, setSingleStartDate] = useState(new Date(new Date().setDate(1)).toISOString().split('T')[0]);
    const [singleEndDate, setSingleEndDate] = useState(new Date().toISOString().split('T')[0]);
    // State to hold the dates that are actually submitted for the report
    const [reportDates, setReportDates] = useState(null);

    // --- State for Comparison Tab ---
    const [compMode, setCompMode] = useState('prev_month');
    const [periodA, setPeriodA] = useState({ 
        start: new Date(new Date().setDate(1)).toISOString().split('T')[0], 
        end: new Date().toISOString().split('T')[0] 
    });
    const [periodB, setPeriodB] = useState({ start: '', end: '' });
    // State to hold the dates for comparison reports
    const [comparisonReportDates, setComparisonReportDates] = useState(null);

    // --- Handlers ---
    const handlePeriodSearch = () => {
        setReportDates({ start: singleStartDate, end: singleEndDate });
    };

    const handleComparisonSearch = () => {
        setComparisonReportDates({ periodA, periodB });
    };

    // --- Effects ---
    // Effect to automatically calculate Period B based on Period A and the comparison mode.
    useEffect(() => {
        if (compMode === 'custom') return;

        const periodA_start_date = new Date(periodA.start);
        const periodA_end_date = new Date(periodA.end);

        let periodB_start, periodB_end;

        if (compMode === 'prev_month') {
            const y = periodA_start_date.getUTCFullYear();
            const m = periodA_start_date.getUTCMonth();
            periodB_start = new Date(Date.UTC(y, m - 1, 1));
            periodB_end = new Date(Date.UTC(y, m, 0));
        } else if (compMode === 'last_year') {
            const start_y = periodA_start_date.getUTCFullYear() - 1;
            const start_m = periodA_start_date.getUTCMonth();
            const start_d = periodA_start_date.getUTCDate();
            periodB_start = new Date(Date.UTC(start_y, start_m, start_d));

            const end_y = periodA_end_date.getUTCFullYear() - 1;
            const end_m = periodA_end_date.getUTCMonth();
            const end_d = periodA_end_date.getUTCDate();
            periodB_end = new Date(Date.UTC(end_y, end_m, end_d));
        }
        
        if (periodB_start && periodB_end) {
            setPeriodB({ 
                start: periodB_start.toISOString().split('T')[0], 
                end: periodB_end.toISOString().split('T')[0] 
            });
        }
    }, [compMode, periodA.start, periodA.end]);

    return (
        <Container fluid className="p-0">
            <div className="page-header">
                <div className="page-header-left">
                    <h3>리포트</h3>
                    <p>기간별 매출 분석과 비교 리포트를 확인합니다</p>
                </div>
            </div>
            <Tabs id="reports-tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                <Tab eventKey="period" title={<><FaCalendarAlt className="me-2" />기간별 분석</>}>
                    <Card className="mb-3">
                        <Card.Body>
                            <Form as={Row} className="align-items-end">
                                <Col md={4}>
                                    <Form.Group controlId="periodStartDate">
                                        <Form.Label>시작일</Form.Label>
                                        <Form.Control type="date" value={singleStartDate} onChange={e => setSingleStartDate(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group controlId="periodEndDate">
                                        <Form.Label>종료일</Form.Label>
                                        <Form.Control type="date" value={singleEndDate} onChange={e => setSingleEndDate(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={4}><Button onClick={handlePeriodSearch} className="w-100"><FaSearch className="me-2" />조회</Button></Col>
                            </Form>
                        </Card.Body>
                    </Card>

                    {reportDates && (
                        <ReportGroup 
                            key={`single-${reportDates.start}-${reportDates.end}`}
                            startDate={reportDates.start} 
                            endDate={reportDates.end} 
                        />
                    )}
                </Tab>
                <Tab eventKey="comparison" title={<><FaChartLine className="me-2" />성과 비교</>}>
                    <Card className="mb-3">
                        <Card.Body>
                            <Row className="align-items-end">
                                <Col md={3}>
                                    <Form.Group controlId="compModeSelect">
                                        <Form.Label>비교 방식</Form.Label>
                                        <Form.Select value={compMode} onChange={e => setCompMode(e.target.value)}>
                                            <option value="prev_month">전월 대비</option>
                                            <option value="last_year">전년 동기 대비</option>
                                            <option value="custom">사용자 지정</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Row>
                                        <Col>
                                            <Form.Group controlId="periodA_start">
                                                <Form.Label>기준 기간 (시작)</Form.Label>
                                                <Form.Control type="date" value={periodA.start} onChange={e => setPeriodA(p => ({ ...p, start: e.target.value }))} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="periodA_end">
                                                <Form.Label>기준 기간 (종료)</Form.Label>
                                                <Form.Control type="date" value={periodA.end} onChange={e => setPeriodA(p => ({ ...p, end: e.target.value }))} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={4}>
                                    <Row>
                                        <Col>
                                            <Form.Group controlId="periodB_start">
                                                <Form.Label>비교 기간 (시작)</Form.Label>
                                                <Form.Control type="date" value={periodB.start} onChange={e => setPeriodB(p => ({ ...p, start: e.target.value }))} disabled={compMode !== 'custom'} />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group controlId="periodB_end">
                                                <Form.Label>비교 기간 (종료)</Form.Label>
                                                <Form.Control type="date" value={periodB.end} onChange={e => setPeriodB(p => ({ ...p, end: e.target.value }))} disabled={compMode !== 'custom'} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md="auto" className="mt-3 mt-md-0">
                                    <Button onClick={handleComparisonSearch} className="w-100">
                                        <FaSearch className="me-2" />비교
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {comparisonReportDates && (
                        <Row>
                            <Col md={6}>
                                <h4>기준 기간 리포트</h4>
                                <p>{comparisonReportDates.periodA.start} ~ {comparisonReportDates.periodA.end}</p>
                                <ReportGroup 
                                    key={`compA-${comparisonReportDates.periodA.start}-${comparisonReportDates.periodA.end}`}
                                    startDate={comparisonReportDates.periodA.start} 
                                    endDate={comparisonReportDates.periodA.end} 
                                />
                            </Col>
                            <Col md={6}>
                                <h4>비교 기간 리포트</h4>
                                <p>{comparisonReportDates.periodB.start} ~ {comparisonReportDates.periodB.end}</p>
                                <ReportGroup 
                                    key={`compB-${comparisonReportDates.periodB.start}-${comparisonReportDates.periodB.end}`}
                                    startDate={comparisonReportDates.periodB.start} 
                                    endDate={comparisonReportDates.periodB.end} 
                                />
                            </Col>
                        </Row>
                    )}
                </Tab>
            </Tabs>
        </Container>
    );
};

export default ReportsPage;
